import SegmentedControl from "./SegmentedControl";
import { ReactElement, ReactNode, useEffect } from "react";
import { atom, useAtom } from "jotai";
import {
  Box,
  Collapse,
  Flex,
  Grid,
  VStack,
  Wrap,
  IconButton,
  Text,
} from "@chakra-ui/react";
import { ChevronLeftIcon, ChevronRightIcon } from "@chakra-ui/icons";
import {
  displayStringDateAtom,
  displayStringDateSmallAtom,
  handleTapDecrementAtom,
  handleTapIncrementAtom,
  resultBaseTimeAtom,
  resultTabSelectAtom,
  selectResultTabAtom,
} from "./ResultAtom";
import { MonthChart, WeekChart, YearChart } from "./ResultChart";
import { Switch } from "@chakra-ui/react";
import dayjs from "dayjs";
import { analysisStartedDateAtom } from "../../../stores/mimosysStore";
import { outputStenScoreAtom } from "../../../store";
import { useModifiedTranslation } from "../../../hooks/useModifiedTranslation";
import { NumericDataText } from "../NumericDataText";

/**
 * 結果画面の中身のコンポーネント
 * 3画面(すべて、元気圧、活量値)で使用
 */
const showMentalActivityAtom = atom(false);
const showVitalityAtom = atom(false);
const showMi1Atom = atom(false);

/*
 * ログイン後 解析結果グラフ画面
 */
export function ResultBody(): ReactElement {
  const [, setSelectResultTab] = useAtom(selectResultTabAtom);
  const [resultTabSelect] = useAtom(resultTabSelectAtom);
  const t = useModifiedTranslation();
  const tabValues = [
    t("Graph.common.twoWeeks"),
    t("Graph.common.month"),
    t("Graph.common.year"),
  ];

  return (
    <VStack w="full" flex={1} gap={4}>
      {/* 期間指定の選択タブ */}
      <SegmentedControl
        activeTextColor="login.login_button_bg"
        inActiveTextColor="primary.theme_lv1"
        inActiveColor="primary.theme_lv2"
        activeColor="primary.theme_lv1"
        value={resultTabSelect}
        onChanged={(value) => setSelectResultTab(value)}
        values={tabValues}
      />

      {/* 期間指定の日程表示 */}
      <ShowDate />

      {/* グラフ表示【左右ナビゲーション・トグル付き】 */}
      <ChartContent />
    </VStack>
  );
}

/*
 * 解析結果グラフ画面 表示期間のタブ表示 / グラフ表示 / トグル表示
 */
function WrapCard(
  props: Readonly<{ children: ReactNode; index: number }>,
): ReactElement {
  const [baseDateTime] = useAtom(resultBaseTimeAtom);
  const [analysisStatedDate] = useAtom(analysisStartedDateAtom);
  const [selectValue] = useAtom(resultTabSelectAtom);
  const [, handleTapIncrement] = useAtom(handleTapIncrementAtom);
  const [, handleTapDecrement] = useAtom(handleTapDecrementAtom);
  const [showMentalActivity, setShowMentalActivity] = useAtom(
    showMentalActivityAtom,
  );
  const [showVitality, setShowVitality] = useAtom(showVitalityAtom);
  const [showMi1, setShowMi1] = useAtom(showMi1Atom);
  const [outputStenScore] = useAtom(outputStenScoreAtom);
  const t = useModifiedTranslation();
  let diffGranularity: "week" | "month" | "year";
  switch (props.index) {
    case 1:
      diffGranularity = "month";
      break;
    case 2:
      diffGranularity = "year";
      break;
    case 0:
    default:
      diffGranularity = "week";
      break;
  }

  const now = dayjs();
  const canDecrement = baseDateTime.isAfter(
    analysisStatedDate,
    diffGranularity,
  );
  const canIncrement = baseDateTime.isBefore(now, diffGranularity);

  return (
    <Collapse in={selectValue === props.index}>
      <Box
        boxShadow="md"
        sx={{
          ".analysis_arrow_left": {
            fontSize: "1.5rem",
            backgroundColor: "primary.bg_lv1",
            paddingLeft: "10px",
          },
          ".analysis_arrow_right": {
            fontSize: "1.5rem",
            backgroundColor: "primary.bg_lv1",
            paddingRight: "10px",
          },
        }}
      >
        <Grid
          w="100%"
          templateAreas={{
            base: '"left . right" "chart chart chart"',
            md: '"left chart right"',
          }}
          templateColumns={{
            base: "auto 1fr auto",
            md: "auto 1fr auto",
          }}
          gap={{ base: 2, md: 0 }}
          alignItems="center"
        >
          <IconButton
            gridArea="left"
            icon={<ChevronLeftIcon />}
            isDisabled={!canDecrement}
            onClick={handleTapDecrement}
            className="analysis_arrow_left"
            aria-label="Decrement"
            data-testid={`chart-decrement-${props.index}`}
          />
          <Box gridArea="chart" w="100%" minW={0}>
            {props.children}
          </Box>
          <IconButton
            gridArea="right"
            icon={<ChevronRightIcon />}
            isDisabled={!canIncrement}
            onClick={handleTapIncrement}
            className="analysis_arrow_right"
            aria-label="Increment"
            data-testid={`chart-increment-${props.index}`}
          />
        </Grid>
        <Wrap
          justify="center"
          mt={2}
          px={{ base: 0, md: 5 }}
          w="100%"
          spacing={2}
        >
          {/* 解析結果グラフ画面の凡例名：こころ指数 */}
          {outputStenScore && (
            <DisplaySwitch
              title={t("Graph.common.mi1Result")}
              show={showMi1}
              style="mi1Result"
              setShow={(value) => setShowMi1(value)}
            />
          )}
          {/* 解析結果グラフ画面の凡例名：心の活量値 */}
          <DisplaySwitch
            title={t("Graph.common.mentalActivity")}
            show={showMentalActivity}
            style="mentalActivity"
            setShow={(value) => setShowMentalActivity(value)}
          />
          {/* 解析結果グラフ画面の凡例名：元気圧 */}
          <DisplaySwitch
            title={t("Graph.common.vitality")}
            show={showVitality}
            style="vitality"
            setShow={(value) => setShowVitality(value)}
          />
        </Wrap>
      </Box>
    </Collapse>
  );
}

/*
 * グラフ表示【左右ナビゲーション・トグル付き】
 */
function ChartContent(): ReactElement {
  const [showMentalActivity, setShowMentalActivity] = useAtom(
    showMentalActivityAtom,
  );
  const [showVitality] = useAtom(showVitalityAtom);
  const [showMi1, setShowMi1] = useAtom(showMi1Atom);

  const [outputStenScore] = useAtom(outputStenScoreAtom);

  useEffect(() => {
    if (outputStenScore) {
      // こころ指数契約ありのとき:こころ指数のみON
      setShowMi1(true);
    } else {
      // こころ指数契約なしのとき:心の活量値のみON
      setShowMentalActivity(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box w="100%" justifyContent="center">
      <WrapCard index={0}>
        <WeekChart
          showMentalActivity={showMentalActivity}
          showVitality={showVitality}
          showMi1={showMi1}
        />
      </WrapCard>
      <WrapCard index={1}>
        <MonthChart
          showMentalActivity={showMentalActivity}
          showVitality={showVitality}
          showMi1={showMi1}
        />
      </WrapCard>
      <WrapCard index={2}>
        <YearChart
          showMentalActivity={showMentalActivity}
          showVitality={showVitality}
          showMi1={showMi1}
        />
      </WrapCard>
    </Box>
  );
}

/*
 * レイアウトパーツ：トグル表示
 */
function DisplaySwitch(
  props: Readonly<{
    title: string;
    show: boolean;
    style: "mi1Result" | "mentalActivity" | "vitality";
    setShow: (value: boolean) => void;
  }>,
): ReactElement {
  return (
    <Flex alignItems="center" justifyContent="center">
      <Text fontSize="sm" pr={2} fontWeight="500">
        {props.title}
      </Text>
      <Switch
        id={`switch-${props.title}`}
        variant="ios"
        colorScheme={props.style}
        isChecked={props.show}
        onChange={(e) => props.setShow(e.target.checked)}
      />
    </Flex>
  );
}

/*
 * レイアウトパーツ：期間指定のタブ選択後の日付表示
 */
function ShowDate(props: Readonly<{ isSmall?: boolean }>): ReactElement {
  const [displayStringDate] = useAtom(
    props.isSmall ? displayStringDateSmallAtom : displayStringDateAtom,
  );
  return (
    <Box
      sx={{
        ".analysis_display_date": {
          width: "100%",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          fontWeight: "500",
          paddingBottom: "15px",
        },
      }}
    >
      <Flex
        justifyContent="center"
        alignItems="center"
        className="analysis_display_date"
      >
        <NumericDataText
          display="inline-flex"
          fontSize="xl"
          letterSpacing={0.5}
          as="div"
          data-testid="chart-display-date"
        >
          {displayStringDate[0]}
        </NumericDataText>
        {displayStringDate.length > 1 && (
          <Flex display="inline-flex" alignItems="flex-end">
            <NumericDataText fontSize="md" as="div" ml={1} mb={0.5}>
              {displayStringDate[1]}
            </NumericDataText>
            <NumericDataText
              display="inline-flex"
              fontSize="xl"
              ml={1}
              as="div"
            >
              -
            </NumericDataText>
            <NumericDataText ml={1} fontSize="xl" letterSpacing={0.5} as="div">
              {displayStringDate[2]}
            </NumericDataText>
            <NumericDataText fontSize="md" as="div" ml={1} mb={0.5}>
              {displayStringDate[3]}
            </NumericDataText>
          </Flex>
        )}
      </Flex>
    </Box>
  );
}
